import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { mergeMessageIntoCache } from './useMessages';
import type { MessageSummary } from '@/api/types';

/**
 * Realtime message payload from Supabase
 * Includes tenant_id which we must verify
 */
interface RealtimeMessagePayload extends MessageSummary {
  tenant_id?: string;
  conversation_id?: string;
}

/**
 * Hook to subscribe to realtime message updates for a conversation
 *
 * @param conversationId - The conversation to subscribe to
 * @param tenantId - The operator's tenant ID for security verification
 * @returns { isConnected } - Whether the realtime connection is active
 */
export function useRealtimeMessages(conversationId: string, tenantId: string) {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Don't subscribe without both IDs
    if (!conversationId || !tenantId) {
      setIsConnected(false);
      return;
    }

    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMessage = payload.new as RealtimeMessagePayload;

          // SECURITY: Verify tenant_id matches before merging
          // Never trust realtime payload blindly
          if (newMessage.tenant_id && newMessage.tenant_id !== tenantId) {
            // Silently ignore - don't expose security check in logs
            return;
          }

          // Extract safe message (remove internal fields)
          const safeMessage: MessageSummary = {
            id: newMessage.id,
            sender_type: newMessage.sender_type,
            sender_operator_id: newMessage.sender_operator_id,
            sanitized_text: newMessage.sanitized_text,
            created_at: newMessage.created_at,
          };

          // Merge into cache
          mergeMessageIntoCache(queryClient, conversationId, safeMessage);
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
      });

    // Cleanup on unmount or dependency change
    return () => {
      supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [conversationId, tenantId, queryClient]);

  return { isConnected };
}
